// Design tokens for the 2026 web redesign.
// Source: Claude Design canvas "GrocerApp web redesign — all screens",
// board "Design guidelines" §10. Components should reference these (via the
// MUI theme or the CSS variables emitted in MainTheme) instead of raw values.

export const color = {
  ink: "#111111", // primary actions, headings, body text
  inkHover: "#333333",
  inkRaised: "#222222", // hover / raised surfaces on black (footer, cart bar)
  text2: "#333333", // product names, secondary body copy
  muted: "#6B6B6B", // captions, meta; never use anything lighter for text
  bg: "#FFFFFF",
  surface: "#F6F6F6", // image tiles, info cards, skeletons
  surfaceHover: "#EFEFEF", // image tiles on hover
  field: "#F3F3F3", // search, inputs, secondary buttons
  fieldHover: "#E8E8E8",
  line: "#EBEBEB", // hairlines, dividers
  lineStrong: "#D6D6D6", // outline buttons, sort control
  brand: "#FF6000", // logo, icons, focus ring, text on black only
  brandText: "#C24400", // orange text on light backgrounds (5.1:1)
  brandTint: "#FFF1E8",
  deal: "#005256", // discount badge
  success: "#0E7A3E",
  successTint: "#EEF6F0",
  danger: "#B3261E",
  dangerHover: "#8C1D17",
  dangerOnDark: "#FF6B5E", // error accents on black (toast dot)
  dangerTint: "#FDECEA",
  footer: "#111111",
  footerText: "#C9D0D3",
  footerLine: "#2A2A2A",
  backdrop: "rgba(17,17,17,.5)"
};

export const font = {
  family:
    "'Noto Sans', 'Noto Nastaliq Urdu', system-ui, -apple-system, 'Segoe UI', sans-serif",
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700, heavy: 800 }
};

// px values; `mobile` overrides apply under breakpoint.sm (720).
// The first block is the guidelines' §3 type scale. The second block holds
// the off-scale sizes the artboards use for specific UI; use them only for
// that UI, never as general text sizes.
export const fontSize = {
  display: 40,
  h1: 32,
  h2: 26,
  h3: 20,
  title: 17,
  body: 15,
  small: 13,
  micro: 12,

  hero: 48, // content-page hero titles (About, Mobile app)
  price: 28, // PDP / plan price
  monogram: 24, // brand-initial fallback tiles
  lead: 18, // hero lead paragraphs, summary total row
  input: 16, // large search inputs (also stops iOS zoom-on-focus)
  ui: 14, // footer links, nav items, chips, field labels, meta lists
  badge: 11, // cart count badge

  mobile: { h1: 26, h2: 22, display: 30, hero: 34 }
};

// font-size for icons (MUI SvgIcon and inline svg)
export const iconSize = {
  xs: 16,
  sm: 18,
  md: 20,
  lg: 24,
  xl: 28,
  xxl: 32
};

// 4px base scale
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  12: 48,
  18: 72
};

export const radius = {
  badge: 6,
  field: 12,
  tile: 16,
  card: 20,
  dialog: 24,
  pill: 999
};

export const shadow = {
  hairline: `inset 0 0 0 1px ${color.line}`,
  hairlineHover: `inset 0 0 0 1px ${color.ink}`,
  selected: `inset 0 0 0 2px ${color.ink}`,
  bar: "0 1px 0 rgba(0,0,0,.08)",
  float: "0 2px 8px rgba(0,0,0,.18)",
  overlay: "0 8px 24px rgba(0,0,0,.25)"
};

// <720 phone · 720–1099 tablet / small laptop · ≥1100 desktop · 1320 container
// ≥1500 wide · ≥1720 extra wide (shopping pages grow to the 1680 wide container)
export const breakpoint = {
  xs: 0,
  sm: 720,
  md: 1100,
  lg: 1320,
  xl: 1920,
  wide: 1500,
  xwide: 1720
};

export const layout = {
  container: 1320,
  // header, footer and browse pages (home, listings, search, brands) use the
  // wide container so big screens show more products instead of empty sides;
  // forms, account and reading pages stay on the 1320 container.
  containerWide: 1680,
  gutter: 32,
  gutterMobile: 16,
  headerHeight: 76,
  pageTop: 24,
  sectionGap: 48,
  titleGap: 16,
  beforeFooter: 72,
  flowAside: 400,
  accountNav: 240,
  readingColumn: 880,
  focusedCard: 440
};

export const control = {
  lg: 56, // sticky cart bar, place order
  md: 48, // default
  sm: 40, // inside cards, steppers
  icon: 44 // icon-only buttons (min touch target)
};

// CSS custom properties, emitted on :root by MainTheme's CssBaseline override.
export const cssVariables = {
  "--ink": color.ink,
  "--ink-hover": color.inkHover,
  "--ink-raised": color.inkRaised,
  "--text-2": color.text2,
  "--muted": color.muted,
  "--bg": color.bg,
  "--surface": color.surface,
  "--field": color.field,
  "--field-hover": color.fieldHover,
  "--line": color.line,
  "--line-strong": color.lineStrong,
  "--brand": color.brand,
  "--brand-text": color.brandText,
  "--brand-tint": color.brandTint,
  "--deal": color.deal,
  "--success": color.success,
  "--success-tint": color.successTint,
  "--danger": color.danger,
  "--danger-hover": color.dangerHover,
  "--danger-on-dark": color.dangerOnDark,
  "--danger-tint": color.dangerTint,
  "--footer-text": color.footerText,
  "--font": font.family,
  "--r-badge": `${radius.badge}px`,
  "--r-field": `${radius.field}px`,
  "--r-tile": `${radius.tile}px`,
  "--r-card": `${radius.card}px`,
  "--r-dialog": `${radius.dialog}px`,
  "--r-pill": `${radius.pill}px`,
  "--hairline": shadow.hairline,
  "--bar": shadow.bar,
  "--float": shadow.float,
  "--overlay": shadow.overlay,
  "--container": `${layout.container}px`,
  "--gutter": `${layout.gutter}px`,
  "--header-h": `${layout.headerHeight}px`
};
